/*
 * indexx.ai/how-master-login-works — how Indexx Zodiac actually works.
 *
 * ── What this replaces ────────────────────────────────────────────────────
 *
 * The old page at this path was written before the redesign and decided its
 * own colours from `localStorage.getItem("selectedTheme") || "dark"`, in ten
 * separate `theme === "dark" ? "#EAECEF" : "#000000"` ternaries. That default
 * was safe only while <DarkMode/> was on screen to make the page dark too —
 * and <DarkMode/> is mounted inside HeaderTest, which redesigned routes no
 * longer render. The result was #EAECEF text on the #F4F4F6 body: a measured
 * contrast of 1.08:1, which is to say an invisible page.
 *
 * This one takes its colours from the same module the rest of the redesign
 * uses, so there is no theme to be out of step with.
 *
 * ── The spec table is checked, not transcribed ────────────────────────────
 *
 * Every row below was verified against indexx-exchange-backend/identity/src
 * rather than copied from the design board. Two did not survive that, and
 * both are noted at the rows themselves. Publishing a number we have not read
 * out of the source would be the same mistake as inventing a balance.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LandingFooter from "./LandingFooter";
import { fetchProducts, ZodiacProduct } from "../../data/zodiacProducts";
import s from "./landing.module.css";

const STEPS = [
  {
    n: "STEP 1",
    head: "Create your Indexx ID",
    body: "Register once, on any product in the ecosystem. That account is your Indexx ID — there is no separate sign-up for each product.",
  },
  {
    n: "STEP 2",
    head: "Open Indexx Zodiac",
    body: "The nine ovals sit in the corner of every product. Open the panel and you see the whole ecosystem, grouped by what it does.",
  },
  {
    n: "STEP 3",
    head: "Arrive already signed in",
    body: "Pick one. You land inside it signed in — no password, no second account, nothing to approve.",
  },
];

/**
 * The spec table.
 *
 * `aside` is the qualifier the design sets after an em dash, dimmed — the
 * part that stops a row being read as a stronger promise than it is.
 */
const SPEC: { term: string; value: string; aside?: string }[] = [
  {
    term: "Protocol",
    value: "OAuth 2.0 / OpenID Connect, authorization code with PKCE",
  },
  { term: "PKCE method", value: "S256 only", aside: "plain is rejected" },
  { term: "Token signing", value: "RS256, verified against a published JWKS" },
  { term: "Redirect URIs", value: "Exact-match allowlist", aside: "no wildcards" },

  /*
   * The design had one row here reading "Access token — 15 minutes".
   *
   * That is the Indexx ID token's lifetime (config.js:74, `access: 15 * 60`)
   * and it is correct, but on a page about signing in across the estate it
   * reads as a claim about the credential your browser is holding, and that
   * is a different token. Every Indexx ID sign-in also posts to
   * /session/legacy (zodiacAuth.js:477, bridgeLegacyKeys defaults on) and
   * stores an estate token minted at 8 hours (jwt.operations.ts:82). The
   * provider's own note at legacy.js:9-15 is blunt about why: the OIDC access
   * token "is NOT a credential the estate accepts".
   *
   * So the single row became two. One number was 32x wrong for what it
   * appeared to describe; naming both is the only version that is true.
   */
  { term: "Indexx ID token", value: "15 minutes" },
  {
    term: "Product token",
    value: "8 hours",
    aside: "the credential products actually accept",
  },

  {
    term: "Refresh token",
    value: "30 days, rotated every use",
    aside: "reuse revokes the family",
  },
  { term: "Your session", value: "30 days" },

  /*
   * Literally what /session/end does (session.js:166-177) — and deliberately
   * not the stronger sentence. It does not revoke refresh families, so a
   * product already holding one keeps working until that token expires.
   * "Signs you out everywhere" would be the comfortable phrasing and a false
   * one.
   */
  {
    term: "Signing out",
    value: "Revokes the session, then clears the cookie",
  },
];

export default function HowItWorks() {
  const [products, setProducts] = useState<ZodiacProduct[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    fetchProducts(ac.signal).then(setProducts);
    return () => ac.abort();
  }, []);

  /* Counted, not typed — same reason as the About page and the counts strip. */
  const count = products.length > 0 ? String(products.length) : "—";

  return (
    <div className={s.root}>
      <main className={`${s.shell} ${s.aboutMain}`}>
        <div className={s.sectionHeadText}>
          <p className={s.eyebrow}>How it works</p>
          <h1 className={`${s.h1About} ${s.display}`}>
            You sign in once.
            <br />
            Nothing asks again.
          </h1>
          <p className={s.aboutLede}>
            With indexx.ai, you only need to sign up or log in once to access our entire
            ecosystem.
          </p>
        </div>

        <section>
          <div className={s.teamHead}>
            <h2 className={`${s.h2About} ${s.display}`}>Three steps</h2>
            <span className={s.teamCount}>Once, then never again</span>
          </div>

          <div className={s.steps}>
            {STEPS.map((step) => (
              <div className={s.step} key={step.n}>
                <span className={`${s.stepNum} ${s.display}`}>{step.n}</span>
                <h3 className={`${s.stepHead} ${s.display}`}>{step.head}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={s.spec}>
          <div className={s.specIntro}>
            <h2 className={s.display}>What is actually happening</h2>
            <p>
              Indexx&nbsp;ID is a standards-based identity provider. Nothing bespoke,
              nothing rolled by hand — the same protocol your bank uses.
            </p>
          </div>

          {/* A description list, because that is what this is: terms and their
              values. It costs nothing and gives the table real structure. */}
          <dl className={s.specList}>
            {SPEC.map((row) => (
              <div className={s.specRow} key={row.term}>
                <dt className={s.specTerm}>{row.term}</dt>
                <dd className={s.specValue}>
                  {row.value}
                  {row.aside ? (
                    <>
                      {" — "}
                      <span className={s.specAside}>{row.aside}</span>
                    </>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={s.closer}>
          <div className={s.closerText}>
            <p className={`${s.closerLead} ${s.display}`}>
              One account. {count} products.
            </p>
            {/*
              The design named Wall Street and Eeny Meeny Miny Moe. ai ai N ai
              belongs in that sentence too — it runs its own database and its
              own API, exactly as the other two do. Three, not two.
            */}
            <p className={s.closerNote}>
              Wall&nbsp;Street, Eeny&nbsp;Meeny&nbsp;Miny&nbsp;Moe and ai&nbsp;ai&nbsp;N&nbsp;ai
              keep their own sign-in as well.
            </p>
          </div>
          <Link className={s.ctaPrimary} to="/auth/signup-email">
            Create an account
          </Link>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
